import restClient from 'restClient'
import {
  VDV_DEFAULT_ALTITUDE_ABOVE_SEA_M,
  VDV_DEFAULT_DISTANCE_TO_SHORELINE_KM,
  VDV_DEFAULT_WIND_SPEED_MS,
} from '../../vandervalk/fetchVanDerValkMountingBom'
import type { StudioSystemType } from 'types/studio/items'
import { generateUUID } from 'util/misc'

export const WIND_SPEED_THRESHOLD_MS = 26

export const dlubalUKWindTimeoutMessage = 'Wind zone fetch timed out. Populated with worst case scenario (High).'
export const dlubalUKWindSpeedTimeoutMessage =
  'Wind speed fetch timed out. Populated with worst case scenario (30.3 m/s).'
export const dlubalUKSnowTimeoutMessage = 'Snow zone fetch timed out. Populated with worst case scenario (Zone 5).'
export const dlubalUKDistanceToSeaFetchFailureMessage =
  'Distance to shoreline could not be loaded from Dlubal. Populated with default (1 km).'

export type FetchDlubalUKDataOptions = {
  /** When true, call the load zones API even if wind/snow caches are already marked successful. */
  force?: boolean
}

/** Van der Valk vendor ``snowZoneIdx`` for MCS UK Zone 5 (worst-case Dlubal timeout fallback). */
export const VDV_DLUBAL_SNOW_ZONE_WORST_CASE_IDX = 4

/**
 * Map UK Dlubal/MCS snow zone (1–5, or 6.5) to Van der Valk ``snowZone`` / vendor ``snowZoneIdx`` (0–5).
 * Studio dropdown: 0 = Zone 1 … 5 = Zone 6.5.
 */
export function mapDlubalUKSnowZoneToVanDerValkIdx(dlubalSnowZone: number): number {
  if (dlubalSnowZone === 6.5) {
    return 5
  }
  const idx = Math.round(dlubalSnowZone) - 1
  if (idx < 0) {
    return 0
  }
  if (idx > 5) {
    return 5
  }
  return idx
}

type DlubalUKDataType = 'wind' | 'snow'

interface DlubalUKCacheData {
  value: number | null
  success: boolean
}

const CACHE_KEYS: Record<DlubalUKDataType, { cacheKey: string; resultKey: string }> = {
  wind: { cacheKey: 'dlubal_uk_wind', resultKey: 'wind_speed_ms' },
  snow: { cacheKey: 'dlubal_uk_snow', resultKey: 'snow_zone' },
}

function getDlubalUKCache(type: DlubalUKDataType): DlubalUKCacheData | null {
  if (!window.projectForm) return null

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const { cacheKey, resultKey } = CACHE_KEYS[type]
  const cached = customData[cacheKey]

  if (!cached) return null

  const hasValue = cached[resultKey] !== null && cached[resultKey] !== undefined
  const isSuccess = cached.success === true

  if (isSuccess && !hasValue) {
    console.warn(`Invalid UK Dlubal cache state: success=true but ${resultKey}=${cached[resultKey]}`)
    return null
  }

  return {
    value: hasValue ? cached[resultKey] : null,
    success: isSuccess,
  }
}

export function getDlubalUKWindCache(): DlubalUKCacheData | null {
  return getDlubalUKCache('wind')
}

export function getDlubalUKSnowCache(): DlubalUKCacheData | null {
  return getDlubalUKCache('snow')
}

/** Elevation (m AMSL) from UK snow load-zone cache, for Van der Valk altitude reset / cache indicator. */
export function getDlubalUKSnowElevationCache(): { success: boolean; value: number | null } {
  if (!window.projectForm) {
    return { success: false, value: null }
  }
  const cached = window.projectForm.getState().values.custom_data?.dlubal_uk_snow as
    | { success?: boolean; elevation?: unknown }
    | undefined
  if (!cached || cached.success !== true) {
    return { success: false, value: null }
  }
  const el = cached.elevation
  if (typeof el === 'number' && Number.isFinite(el)) {
    return { success: true, value: el }
  }
  if (el != null && el !== '') {
    const n = Number(el)
    if (Number.isFinite(n)) {
      return { success: true, value: n }
    }
  }
  return { success: false, value: null }
}

function setDlubalUKCacheSuccess(type: DlubalUKDataType, value: number): void {
  if (!window.projectForm) return

  const { cacheKey, resultKey } = CACHE_KEYS[type]

  if (typeof value !== 'number' || isNaN(value)) {
    console.error(`Invalid value for UK Dlubal cache: ${value}`)
    return
  }

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}

  const updatedCustomData = {
    ...customData,
    [cacheKey]: {
      [resultKey]: value,
      success: true,
    },
  }

  window.projectForm.change('custom_data', updatedCustomData)
}

function parseElevationFromLoadZones(elevation: unknown): number | undefined {
  if (typeof elevation === 'number' && Number.isFinite(elevation)) {
    return Math.round(elevation * 100) / 100
  }
  if (typeof elevation === 'string' && elevation.trim() !== '') {
    const n = Number(elevation.trim())
    if (Number.isFinite(n)) {
      return Math.round(n * 100) / 100
    }
  }
  return undefined
}

/** Distance to shoreline (km) from UK wind load-zone cache, for Van der Valk fetch / cache indicator. */
export function getDlubalUKDistanceToSeaCache(): { success: boolean; value: number | null } {
  if (!window.projectForm) {
    return { success: false, value: null }
  }
  const cached = window.projectForm.getState().values.custom_data?.dlubal_uk_wind as
    | { success?: boolean; distance_to_sea?: unknown }
    | undefined
  if (!cached || cached.success !== true) {
    return { success: false, value: null }
  }
  const km = parseDistanceToSeaKmFromLoadZones(cached.distance_to_sea)
  if (km !== undefined) {
    return { success: true, value: km }
  }
  return { success: false, value: null }
}

export function parseDistanceToSeaKmFromLoadZones(distanceToSea: unknown): number | undefined {
  if (typeof distanceToSea === 'number' && Number.isFinite(distanceToSea)) {
    return Math.round(distanceToSea * 100) / 100
  }
  if (typeof distanceToSea === 'string' && distanceToSea.trim() !== '') {
    const n = Number(distanceToSea.trim())
    if (Number.isFinite(n)) {
      return Math.round(n * 100) / 100
    }
  }
  return undefined
}

/** Persist snow_zone + optional elevation; auto-fill Van der Valk altitude when elevation is known. */
function setDlubalUKSnowCacheFromLoadZones(result: { snow_zone: number; elevation?: unknown }): void {
  if (!window.projectForm) return

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const { cacheKey } = CACHE_KEYS.snow
  const prev = (customData[cacheKey] as Record<string, unknown> | undefined) || {}

  const elevationM = parseElevationFromLoadZones(result.elevation)
  const next: Record<string, unknown> = {
    ...prev,
    snow_zone: result.snow_zone,
    success: true,
  }
  if (elevationM !== undefined) {
    next.elevation = elevationM
    delete next.elevation_unavailable
    applyVanDerValkAltitudeFromDlubalElevationM(elevationM)
  } else {
    delete next.elevation
    next.elevation_unavailable = true
    applyVanDerValkAltitudeFallbackIfUnset()
  }

  window.projectForm.change('custom_data', {
    ...customData,
    [cacheKey]: next,
  })
}

type VanDerValkIntegrationNumericField = 'altitudeAboveSea' | 'distanceToShorelineKM' | 'windSpeedMs'

function applyVanDerValkIntegrationField(field: VanDerValkIntegrationNumericField, value: number): void {
  if (!Number.isFinite(value)) return
  const system = window.editor?.selectedSystem as StudioSystemType | undefined
  if (!system || system.mounting !== 'vanDerValk') return

  const rounded = Math.round(value * 100) / 100
  const base = (system.integration_json ?? {}) as Record<string, unknown>
  const prevVdv = base.vanDerValk
  const vdv =
    typeof prevVdv === 'object' && prevVdv !== null ? (prevVdv as Record<string, unknown>) : {}
  if (prevVdv !== vdv) {
    base.vanDerValk = vdv
  }
  vdv[field] = rounded
  if (!system.integration_json) {
    system.integration_json = base as StudioSystemType['integration_json']
  }
  window.editor.signals.objectChanged.dispatch(system, 'mounting')
}

function isVanDerValkIntegrationFieldUnset(current: unknown): boolean {
  return current === undefined || current === null || current === ''
}

function applyVanDerValkIntegrationFieldFallbackIfUnset(
  field: VanDerValkIntegrationNumericField,
  value: number
): void {
  const system = window.editor?.selectedSystem as StudioSystemType | undefined
  if (!system || system.mounting !== 'vanDerValk') {
    return
  }
  const prevVdv = (system.integration_json as { vanDerValk?: Record<string, unknown> } | null | undefined)
    ?.vanDerValk
  if (!isVanDerValkIntegrationFieldUnset(prevVdv?.[field])) {
    return
  }
  applyVanDerValkIntegrationField(field, value)
}

/** When UK load zones return elevation, mirror into Van der Valk mounting integration (m AMSL). */
function applyVanDerValkAltitudeFromDlubalElevationM(elevationM: number): void {
  applyVanDerValkIntegrationField('altitudeAboveSea', elevationM)
}

/** When UK load zones return distance_to_sea, mirror into Van der Valk mounting integration (km). */
function applyVanDerValkDistanceToShorelineFromDlubalKm(distanceKm: number): void {
  applyVanDerValkIntegrationField('distanceToShorelineKM', distanceKm)
}

/** Apply product default distance to shoreline when Dlubal did not return a value and the field is still empty. */
export function applyVanDerValkDistanceToShorelineFallbackIfUnset(): void {
  applyVanDerValkIntegrationFieldFallbackIfUnset('distanceToShorelineKM', VDV_DEFAULT_DISTANCE_TO_SHORELINE_KM)
}

/** Apply default wind speed when Dlubal did not return a value and the field is still empty. */
export function applyVanDerValkWindSpeedFallbackIfUnset(): void {
  applyVanDerValkIntegrationFieldFallbackIfUnset('windSpeedMs', VDV_DEFAULT_WIND_SPEED_MS)
}

/** Apply default altitude when Dlubal did not return elevation and the field is still empty. */
export function applyVanDerValkAltitudeFallbackIfUnset(): void {
  applyVanDerValkIntegrationFieldFallbackIfUnset('altitudeAboveSea', VDV_DEFAULT_ALTITUDE_ABOVE_SEA_M)
}

/** Apply Van der Valk Dlubal fallbacks for BOM-required fields that are still unset after fetch. */
export function applyVanDerValkDlubalFetchFallbacksIfUnset(): void {
  applyVanDerValkWindSpeedFallbackIfUnset()
  applyVanDerValkAltitudeFallbackIfUnset()
  applyVanDerValkDistanceToShorelineFallbackIfUnset()
}

/** Persist wind_speed_ms + optional distance_to_sea; auto-fill Van der Valk shoreline distance when known. */
function setDlubalUKWindCacheFromLoadZones(result: { wind_speed_ms: number; distance_to_sea?: unknown }): void {
  if (!window.projectForm) return

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const { cacheKey } = CACHE_KEYS.wind
  const prev = (customData[cacheKey] as Record<string, unknown> | undefined) || {}

  const distanceKm = parseDistanceToSeaKmFromLoadZones(result.distance_to_sea)
  const next: Record<string, unknown> = {
    ...prev,
    wind_speed_ms: result.wind_speed_ms,
    success: true,
  }
  if (distanceKm !== undefined) {
    next.distance_to_sea = distanceKm
    delete next.distance_to_sea_unavailable
    applyVanDerValkDistanceToShorelineFromDlubalKm(distanceKm)
  } else {
    delete next.distance_to_sea
    next.distance_to_sea_unavailable = true
    applyVanDerValkDistanceToShorelineFallbackIfUnset()
  }

  window.projectForm.change('custom_data', {
    ...customData,
    [cacheKey]: next,
  })
}

function setDlubalUKCacheFailure(type: DlubalUKDataType): void {
  if (!window.projectForm) return

  const { cacheKey, resultKey } = CACHE_KEYS[type]
  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}

  const updatedCustomData = {
    ...customData,
    [cacheKey]: {
      [resultKey]: null,
      success: false,
    },
  }

  window.projectForm.change('custom_data', updatedCustomData)
}

let ongoingRequest: Promise<void> | null = null

/** True when wind/snow caches predate distance_to_sea / elevation backfill and need one more API call. */
function needsDlubalUKExtendedFieldBackfill(): boolean {
  if (!window.projectForm) return false

  const customData = window.projectForm.getState().values.custom_data || {}
  const snowEntry = customData.dlubal_uk_snow as Record<string, unknown> | undefined

  if (getDlubalUKWindCache()?.success === true && !getDlubalUKDistanceToSeaCache().success) {
    return true
  }

  if (getDlubalUKSnowCache()?.success === true) {
    if (snowEntry && !('elevation' in snowEntry) && snowEntry.elevation_unavailable !== true) {
      return true
    }
  }

  return false
}

export async function fetchDlubalUKData(options?: FetchDlubalUKDataOptions): Promise<void> {
  if (!window.projectForm) {
    console.error('ProjectForm not available for UK Dlubal data fetch')
    return
  }

  const windCache = getDlubalUKWindCache()
  const snowCache = getDlubalUKSnowCache()
  const distanceSeaCache = getDlubalUKDistanceToSeaCache()
  const elevationCache = getDlubalUKSnowElevationCache()
  const canSkipCachedFetch =
    !options?.force &&
    windCache?.success === true &&
    snowCache?.success === true &&
    distanceSeaCache.success === true &&
    elevationCache.success === true &&
    !needsDlubalUKExtendedFieldBackfill()

  if (canSkipCachedFetch) {
    return
  }

  if (ongoingRequest) {
    return ongoingRequest
  }

  ongoingRequest = (async () => {
    try {
      const formState = window.projectForm.getState()
      const { id: projectId, org_id: orgId } = formState.values
      if (!projectId || !orgId) {
        console.error('Project ID or Org ID not available for UK Dlubal fetch')
        return
      }

      const restClientInstance = restClient(window.API_ROOT + '/api')
      const url = `projects/${projectId}/structural/get_load_zones/`

      const res = await restClientInstance('CUSTOM_GET', 'loadDlubalUKData', { url })
      if (!res.data?.success) {
        throw Error('UK Dlubal load zones fetch failed')
      }

      const result = res.data.result

      if (result.wind_speed_ms !== undefined && result.wind_speed_ms !== null) {
        setDlubalUKWindCacheFromLoadZones({
          wind_speed_ms: Math.trunc(result.wind_speed_ms * 100) / 100,
          distance_to_sea: result.distance_to_sea,
        })
      } else {
        setDlubalUKCacheFailure('wind')
        applyVanDerValkWindSpeedFallbackIfUnset()
      }

      if (result.snow_zone !== undefined && result.snow_zone !== null) {
        const snowZoneNum = typeof result.snow_zone === 'number' ? result.snow_zone : Number(result.snow_zone)
        if (Number.isFinite(snowZoneNum)) {
          setDlubalUKSnowCacheFromLoadZones({
            snow_zone: snowZoneNum,
            elevation: result.elevation,
          })
        } else {
          setDlubalUKCacheFailure('snow')
          applyVanDerValkAltitudeFallbackIfUnset()
        }
      } else {
        setDlubalUKCacheFailure('snow')
        applyVanDerValkAltitudeFallbackIfUnset()
      }
    } catch (err) {
      console.error('UK Dlubal load zones fetch failed\n', err)
      setDlubalUKCacheFailure('wind')
      setDlubalUKCacheFailure('snow')
      applyVanDerValkDlubalFetchFallbacksIfUnset()
    } finally {
      ongoingRequest = null
    }
  })()

  return ongoingRequest
}

export async function autoFetchDlubalUKDataIfNeeded(): Promise<void> {
  type DlubalUKAutoFetchConfig = {
    dataKey: string
    resultKey: string
    isCached: () => boolean
  }

  const dlubalDataConfigs: DlubalUKAutoFetchConfig[] = [
    {
      dataKey: 'dlubal_uk_wind',
      resultKey: 'wind_speed_ms',
      isCached: () => getDlubalUKWindCache()?.success === true,
    },
    {
      dataKey: 'dlubal_uk_snow',
      resultKey: 'snow_zone',
      isCached: () => getDlubalUKSnowCache()?.success === true,
    },
    {
      dataKey: 'dlubal_uk_wind',
      resultKey: 'distance_to_sea',
      isCached: () => getDlubalUKDistanceToSeaCache().success,
    },
    {
      dataKey: 'dlubal_uk_snow',
      resultKey: 'elevation',
      isCached: () => getDlubalUKSnowElevationCache().success,
    },
  ]

  const missingDataConfigs = dlubalDataConfigs.filter((config) => !config.isCached())

  if (missingDataConfigs.length === 0 && !needsDlubalUKExtendedFieldBackfill()) {
    return
  }

  const configsWithRequestIds = missingDataConfigs.map((config) => ({
    ...config,
    requestId: generateUUID(),
  }))

  configsWithRequestIds.forEach((config) => {
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_START',
      dataKey: config.dataKey,
      resultKey: config.resultKey,
      requestId: config.requestId,
    })
  })

  try {
    await fetchDlubalUKData()

    const latestCustomData = window.projectForm?.getState().values.custom_data || {}

    configsWithRequestIds.forEach((config) => {
      const cachedData = latestCustomData[config.dataKey]

      window.reduxStore?.dispatch({
        type: 'MOUNTING_AUTO_FETCH_COMPLETE',
        dataKey: config.dataKey,
        resultKey: config.resultKey,
        requestId: config.requestId,
        success: !!cachedData?.[config.resultKey],
        cachedData: cachedData?.[config.resultKey],
      })
    })
  } catch (error) {
    console.error('Error during auto-fetch of UK Dlubal data:', error)

    configsWithRequestIds.forEach((config) => {
      window.reduxStore?.dispatch({
        type: 'MOUNTING_AUTO_FETCH_COMPLETE',
        dataKey: config.dataKey,
        resultKey: config.resultKey,
        requestId: config.requestId,
        success: false,
      })
    })
  }
}

let scheduledAutoFetch: Promise<void> | null = null

/** Deduped entry point for mount-time UK Dlubal auto-fetch from multiple mounting fields. */
export function requestAutoFetchDlubalUKDataIfNeeded(): Promise<void> {
  if (scheduledAutoFetch) {
    return scheduledAutoFetch
  }
  scheduledAutoFetch = autoFetchDlubalUKDataIfNeeded().finally(() => {
    scheduledAutoFetch = null
  })
  return scheduledAutoFetch
}
